import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2, Plus, Pencil, Ban, RefreshCw, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const PLAN_OPTIONS = [
  { value: "assicurazioni", label: "Assicurazioni (€19/mese · setup €99)" },
  { value: "professional", label: "Professional (€29/mese · setup €149)" },
  { value: "enterprise", label: "Enterprise Multi-Sede (€69/mese · setup €399)" },
];

const STATUS_LABELS = {
  trial: { txt: "Prova 14gg", cls: "bg-amber-100 text-amber-800" },
  active: { txt: "Attivo (pagante)", cls: "bg-emerald-100 text-emerald-800" },
  admin_gift: { txt: "Regalo admin", cls: "bg-violet-100 text-violet-800" },
  expired: { txt: "Scaduto", cls: "bg-slate-100 text-slate-500" },
  canceled: { txt: "Cancellato", cls: "bg-rose-100 text-rose-700" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

function AssignDialog({ consultants, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [consultantId, setConsultantId] = useState("");
  const [plan, setPlan] = useState("professional");
  const [cycle, setCycle] = useState("annual");
  const [duration, setDuration] = useState(1);
  const [status, setStatus] = useState("admin_gift");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!consultantId) return toast.error("Seleziona un consulente");
    setLoading(true);
    try {
      await api.post("/admin/subscriptions", {
        consultant_id: consultantId,
        plan, billing_cycle: cycle, duration_months: parseInt(duration),
        status, admin_notes: notes,
      });
      toast.success("Abbonamento assegnato");
      setOpen(false);
      onAssigned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="open-assign-sub-btn" className="inline-flex items-center gap-2 bg-[#2456C6] hover:bg-[#1B47AC] text-white text-sm font-bold px-4 py-2 rounded-xl">
          <Plus className="w-4 h-4" /> Assegna abbonamento
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Assegna piano a consulente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Consulente</Label>
            <Select value={consultantId} onValueChange={setConsultantId}>
              <SelectTrigger data-testid="assign-consultant-select"><SelectValue placeholder="Scegli consulente" /></SelectTrigger>
              <SelectContent>
                {consultants.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.studio_name} · {c.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Piano</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="assign-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ciclo</Label>
              <Select value={cycle} onValueChange={setCycle}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensile</SelectItem>
                  <SelectItem value="annual">Annuale</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Durata (mesi)</Label>
              <Input type="number" min="1" max="60" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="assign-duration-input" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin_gift">Regalo Admin</SelectItem>
                  <SelectItem value="trial">Prova</SelectItem>
                  <SelectItem value="active">Attivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Note (es. "Promo lancio")</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="assign-notes-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="assign-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assegna"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ sub, onSaved }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(sub.plan);
  const [status, setStatus] = useState(sub.status);
  const [extendMonths, setExtendMonths] = useState(0);
  const [notes, setNotes] = useState(sub.admin_notes || "");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/subscriptions/${sub.id}`, {
        plan, status, admin_notes: notes,
        extend_months: parseInt(extendMonths) || 0,
      });
      toast.success("Abbonamento aggiornato");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-sub-${sub.id}`} className="p-2 hover:bg-slate-100 rounded-md" title="Modifica">
          <Pencil className="w-4 h-4 text-slate-600" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Modifica abbonamento</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{sub.consultant_studio} · scade {fmtDate(sub.current_period_end)}</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Piano</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Stato</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trial">Prova</SelectItem>
                <SelectItem value="active">Attivo</SelectItem>
                <SelectItem value="admin_gift">Regalo Admin</SelectItem>
                <SelectItem value="canceled">Cancellato</SelectItem>
                <SelectItem value="expired">Scaduto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Estendi di N mesi (opzionale)</Label>
            <Input type="number" min="0" max="120" value={extendMonths} onChange={(e) => setExtendMonths(e.target.value)} />
          </div>
          <div>
            <Label>Note</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSubscriptions() {
  const [subs, setSubs] = useState(null);
  const [consultants, setConsultants] = useState([]);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/subscriptions"),
        api.get("/admin/consultants"),
      ]);
      setSubs(s.data);
      setConsultants(c.data);
    } catch (e) { setSubs([]); }
  };
  useEffect(() => { load(); }, []);

  const cancelSub = async (sub) => {
    if (!window.confirm(`Cancellare l'abbonamento di ${sub.consultant_studio}?`)) return;
    try {
      await api.delete(`/admin/subscriptions/${sub.id}`);
      toast.success("Cancellato");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <DashboardLayout role="superadmin">
      <div className="p-4 sm:p-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-1">Abbonamenti</h1>
            <p className="text-sm text-slate-500">Gestisci i piani attivi e assegna piani regalo</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#2456C6] px-3 py-2" data-testid="refresh-subs-btn">
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
            <AssignDialog consultants={consultants} onAssigned={load} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-4 py-3">Studio</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Piano</th>
                <th className="px-4 py-3">Ciclo</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3">Scadenza</th>
                <th className="px-4 py-3">Note</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {subs === null ? (
                <tr><td colSpan={8} className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#2456C6] mx-auto" /></td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={8} className="p-12 text-center text-slate-500">Nessun abbonamento. Crea il primo!</td></tr>
              ) : (
                subs.map((s) => {
                  const sl = STATUS_LABELS[s.status] || { txt: s.status, cls: "bg-slate-100" };
                  return (
                    <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sub-row-${s.id}`}>
                      <td className="px-4 py-3 font-bold text-[#1E293B]">{s.consultant_studio}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.consultant_email}</td>
                      <td className="px-4 py-3 text-sm font-bold">{s.plan_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.billing_cycle === "annual" ? "Annuale" : "Mensile"}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${sl.cls}`}>{sl.txt}</span></td>
                      <td className="px-4 py-3 text-sm">{fmtDate(s.current_period_end)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{s.admin_notes || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <EditDialog sub={s} onSaved={load} />
                          {s.status !== "canceled" && (
                            <button onClick={() => cancelSub(s)} data-testid={`cancel-sub-${s.id}`} className="p-2 hover:bg-rose-50 rounded-md" title="Cancella">
                              <Ban className="w-4 h-4 text-rose-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
